import {
  AlertCircle,
  ArrowLeft,
  BadgeCheck,
  Calendar as CalendarIcon,
  CircleAlert,
  Euro,
  FileText,
  Flag,
  Gauge,
  Pencil,
  Timer,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Badge, Button, Card, Col, Container, Nav, Row, Spinner, Tab } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchWorkPackagesByProject } from "../../../redux/slices/workPackageSlice";
import WorkPackages from "../workpackage/WorkPackages";
import i18n from "../../../i18n";
import { deleteProject, fetchProject, updateProject } from "../../../redux/slices/projectsSlice";
import Calendar from "../../calendar/Calendar/Calendar";
import GanttChart from "../../graphs/GanttChart/GanttChart";
import ProjectMembers from "../../projectMembers/ProjectMembers/ProjectMembers";
import CreateEditProjectModal from "../CreateEditProjectModal/CreateEditProjectModal";
import "../projectsFeature.css";

const priorityColors = {
  low: "secondary",
  medium: "info",
  high: "warning",
  urgent: "danger",
};

const formatDate = (value) => {
  if (!value) {
    return "-";
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return "-";
  }

  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(parsed);
};

const ProjectPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const { item: project, isLoading } = useSelector((state) => state.projects);
  const [activeTab, setActiveTab] = useState("calendar");
  const [showEditModal, setShowEditModal] = useState(false);
  const [editProjectData, setEditProjectData] = useState({});
  const [isDeleting, setIsDeleting] = useState(false);
  const { items: workPackages, isLoading: isLoadingWorkPackages } = useSelector((state) => state.workPackages);

  useEffect(() => {
    if (id) {
      dispatch(fetchProject(id));
      dispatch(fetchWorkPackagesByProject(id));
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (project) {
      setEditProjectData(project);
    }
  }, [project]);

  const projectSummary = useMemo(() => {
    if (!project) {
      return [];
    }

    return [
      {
        key: "project_type",
        icon: <BadgeCheck size={16} />,
        label: i18n.t("fields.Project.project_type"),
        value: i18n.t(`fields.Project.project_type_enum.${String(project?.project_type || "").toLowerCase()}`),
      },
      {
        key: "status",
        icon: <Flag size={16} />,
        label: i18n.t("fields.Project.status"),
        value: i18n.t(`fields.Project.status_enum.${String(project?.status || "").toLowerCase()}`),
      },
      {
        key: "date_range",
        icon: <CalendarIcon size={16} />,
        label: i18n.t("fields.Project.date_range"),
        value: `${formatDate(project?.start_date)} - ${formatDate(project?.end_date)}`,
      },
      {
        key: "priority",
        icon: <CircleAlert size={16} />,
        label: i18n.t("fields.Project.priority"),
        value: i18n.t(`fields.Project.priority_enum.${String(project?.priority || "").toLowerCase()}`),
        badge: priorityColors[String(project?.priority || "").toLowerCase()] || "secondary",
      },
      {
        key: "estimated_etis",
        icon: <Timer size={16} />,
        label: i18n.t("fields.Project.estimated_etis"),
        value: `${project?.estimated_etis || 0} ETIs`,
      },
      {
        key: "budget",
        icon: <Euro size={16} />,
        label: i18n.t("fields.Project.budget"),
        value: `${project?.budget || 0} ${i18n.t("common.units.euros_symbol")}`,
      },
    ];
  }, [project]);

  const handleGoBack = () => {
    navigate("/projects");
  };

  const handleDelete = async () => {
    if (!project?.id) {
      return;
    }

    const confirmed = window.confirm(i18n.t("components.ProjectPage.confirmDelete"));
    if (!confirmed) {
      return;
    }

    setIsDeleting(true);
    const result = await dispatch(deleteProject(project.id));
    setIsDeleting(false);

    if (!result.error) {
      navigate("/projects");
    }
  };

  const handleUpdateProject = async (payload) => {
    if (!project?.id) {
      return;
    }

    const result = await dispatch(
      updateProject({
        ...payload,
        id: project.id,
      })
    );

    if (!result.error) {
      setShowEditModal(false);
    }
  };

  if (isLoading && !project) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" />
      </Container>
    );
  }

  if (!project) {
    return (
      <Container className="py-5">
        <Card className="border-0 shadow-sm">
          <Card.Body className="d-flex align-items-center gap-2">
            <AlertCircle size={18} />
            <span>{i18n.t("components.ProjectPage.notFound")}</span>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return (
    <Container fluid className="project-page-shell py-4 px-3 px-md-4 px-lg-5 fade-in-up">
      <CreateEditProjectModal
        showModal={showEditModal}
        setShowModal={setShowEditModal}
        onSubmit={handleUpdateProject}
        projectData={editProjectData}
        setProjectData={setEditProjectData}
        isCreate={false}
        isSubmitting={isLoading}
      />

      <section className="project-hero mb-4">
        <div className="d-flex flex-column flex-lg-row justify-content-between gap-3">
          <div>
            <Button variant="link" className="project-back-btn p-0 mb-3" onClick={handleGoBack}>
              <ArrowLeft size={18} />
              <span>{i18n.t("common.actions.go_back")}</span>
            </Button>
            <h1 className="project-title mb-2">{project?.name || i18n.t("components.ProjectPage.untitled")}</h1>
            <p className="project-subtitle mb-0">{project?.description || i18n.t("components.ProjectPage.noDescription")}</p>
          </div>

          <div className="d-flex align-items-start gap-2">
            <Button variant="outline-primary" className="project-action-btn" onClick={() => setShowEditModal(true)}>
              <Pencil size={16} />
              <span>{i18n.t("common.actions.update")}</span>
            </Button>
            <Button
              variant="outline-danger"
              className="project-action-btn"
              onClick={handleDelete}
              disabled={isDeleting}
            >
              <AlertCircle size={16} />
              <span>{i18n.t("common.actions.delete")}</span>
            </Button>
          </div>
        </div>
      </section>

      <Row className="g-3 mb-4">
        {projectSummary.map((entry) => (
          <Col xs={12} md={6} xl={4} key={entry.key}>
            <Card className="project-meta-card h-100">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <div className="d-flex align-items-center gap-2 project-meta-title">
                    {entry.icon}
                    <span>{entry.label}</span>
                  </div>
                  {entry.badge && <Badge bg={entry.badge}>{entry.value}</Badge>}
                </div>
                {!entry.badge && <p className="project-meta-value mb-0">{entry.value}</p>}
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      <Card className="project-description-card mb-4">
        <Card.Header>{i18n.t("fields.Project.description")}</Card.Header>
        <Card.Body>{project?.description || i18n.t("components.ProjectPage.noDescription")}</Card.Body>
      </Card>

      <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
        <Nav className="project-tabs mb-3">
          <Nav.Item>
            <Nav.Link eventKey="calendar">{i18n.t("components.ProjectPage.tabs.calendar")}</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="members">{i18n.t("components.ProjectPage.tabs.members")}</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="documents">{i18n.t("components.ProjectPage.tabs.documents")}</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="workPackages">{i18n.t("components.ProjectPage.tabs.workPackages")}</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="gantt">{i18n.t("components.ProjectPage.tabs.gantt")}</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="statistics">{i18n.t("components.ProjectPage.tabs.statistics")}</Nav.Link>
          </Nav.Item>
        </Nav>

        <Tab.Content>
          <Tab.Pane eventKey="calendar" className="project-pane fade-in-up">
            <Card className="project-pane-card">
              <Card.Body>
                <Calendar />
              </Card.Body>
            </Card>
          </Tab.Pane>

          <Tab.Pane eventKey="members" className="project-pane fade-in-up">
            <Card className="project-pane-card">
              <Card.Body>
                <ProjectMembers project={project} />
              </Card.Body>
            </Card>
          </Tab.Pane>
          <Tab.Pane eventKey="workPackages" className="project-pane fade-in-up">
            <Card className="project-pane-card">
              <Card.Body>
                <WorkPackages project={project} workPackages={workPackages} isLoading={isLoadingWorkPackages} />
              </Card.Body>
            </Card>
          </Tab.Pane>

          <Tab.Pane eventKey="documents" className="project-pane fade-in-up">
            <Card className="project-pane-card">
              <Card.Body className="project-docs-placeholder">
                <FileText size={40} />
                <p className="mb-0">{i18n.t("components.ProjectPage.documentsPlaceholder")}</p>
              </Card.Body>
            </Card>
          </Tab.Pane>

          <Tab.Pane eventKey="gantt" className="project-pane fade-in-up">
            <Card className="project-pane-card">
              <Card.Body className="p-0">
                {activeTab === "gantt" && <GanttChart workPackages={workPackages} />}
              </Card.Body>
            </Card>
          </Tab.Pane>

          <Tab.Pane eventKey="statistics" className="project-pane fade-in-up">
            <Row className="g-3">
              <Col xs={12} md={6}>
                <Card className="project-stat-card h-100">
                  <Card.Body>
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <Gauge size={16} />
                      <strong>{i18n.t("components.ProjectPage.stats.burndownTitle")}</strong>
                    </div>
                    <p className="mb-0 text-muted">{i18n.t("components.ProjectPage.stats.burndownDesc")}</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col xs={12} md={6}>
                <Card className="project-stat-card h-100">
                  <Card.Body>
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <Timer size={16} />
                      <strong>{i18n.t("components.ProjectPage.stats.allocationTitle")}</strong>
                    </div>
                    <p className="mb-0 text-muted">{i18n.t("components.ProjectPage.stats.allocationDesc")}</p>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>
    </Container>
  );
};

export default ProjectPage;